import type { RawAxiosRequestHeaders } from 'axios';
import { AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import { getAuth } from 'firebase/auth';

import { environment } from '../environments/environment';

import { axiosInstance } from './axiosConfig';

class Api {
	public baseURL: string;
	constructor(url: string) {
		this.baseURL = url;
	}
	private async request<Res = any>(
		path: string,
		postBody: any = {},
		method: Method = 'get'
	): Promise<Res> {
		const endpoint = this.baseURL + path;
		const token = await getAuth()?.currentUser?.getIdToken();

		// Don't set Content-Type for FormData - let the browser set it with proper boundary
		const headers: RawAxiosRequestHeaders = {
			Authorization: `Bearer ${token}`,
		};

		// Only set Content-Type for non-FormData requests
		if (!(postBody instanceof FormData)) {
			headers['Content-Type'] = 'application/json';
		}

		const response = await axiosInstance.request<Res>({
			url: endpoint,
			data: postBody,
			method,
			headers,
		});

		return response.data;
	}

	public async makeRequest<Res = any>(
		path: string,
		config: AxiosRequestConfig
	): Promise<AxiosResponse<Res, any>> {
		const endpoint = this.baseURL + path;
		const token = await getAuth()?.currentUser?.getIdToken();
		const headers = config.headers ?? {};
		headers['Authorization'] = `Bearer ${token}`;

		const response = await axiosInstance.request<Res>({
			...config,
			url: endpoint,
			headers,
		});
		return response;
	}

	public get<Res = any>(path: string, postBody: any = {}): Promise<Res> {
		return this.request<Res>(path, postBody, 'get');
	}

	public post<Res = any>(path: string, postBody: any = {}): Promise<Res> {
		return this.request<Res>(path, postBody, 'post');
	}

	public put<Res = any>(path: string, postBody: any = {}): Promise<Res> {
		return this.request<Res>(path, postBody, 'put');
	}

	public patch<Res = any>(path: string, postBody: any = {}): Promise<Res> {
		return this.request<Res>(path, postBody, 'patch');
	}

	public delete<Res = any>(path: string, postBody: any = {}): Promise<Res> {
		return this.request<Res>(path, postBody, 'delete');
	}
}

const driverApi = new Api(environment.baseUrl);

export default driverApi;
